import { useCallback, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { getInvoiceDetailsCardInfo } from "./invoice-helper";
import { invoiceActions } from "src/context/invoice-slice";
import { fetchInvoicesData } from "src/services/invoice-api";
import { useToast } from "src/hooks/useToast";
import InvoiceApprovalDetailCard from "../invoice-homepage/InvoiceApprovalDetailCard";
import { Spinner } from "src/components";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import usePermission from "src/hooks/usePermission";

interface InvoiceApprovalContainerProps {
  searchCategory: string;
  searchValue: string;
}

const InvoiceApprovalContainer = ({ searchCategory, searchValue }: InvoiceApprovalContainerProps) => {
  const { invoiceApprovalList, InvoiceApprovalFilters, invoiceAnalysisData } = useSelector((state: RootState) => state.invoice);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { toast } = useToast();

   const {hasPermission:hasViewDetailsPermission, loading:detailsPermissionLoading}=usePermission({
      module:fineGrainedPermissionsKeys.INVOICE["VIEW DETAILS"].module,
      action:fineGrainedPermissionsKeys.INVOICE["VIEW DETAILS"].action
    })
  
    const { hasPermission: hasPermissionReassign, loading: reassignPermissionLoading } = usePermission({
      module: fineGrainedPermissionsKeys.INVOICE.REASSIGN.module,
      action: fineGrainedPermissionsKeys.INVOICE.REASSIGN.action,
    });
    const { hasPermission: hasPermissionPutOnHold, loading: putOnHoldPermissionLoading } = usePermission({
      module: fineGrainedPermissionsKeys.INVOICE["PUT ON HOLD"].module,
      action: fineGrainedPermissionsKeys.INVOICE["PUT ON HOLD"].action,
    });
    const { hasPermission: hasPermissionReject, loading: rejectPermissionLoading } = usePermission({
      module: fineGrainedPermissionsKeys.INVOICE.REJECT.module,
      action: fineGrainedPermissionsKeys.INVOICE.REJECT.action,
    });
    const { hasPermission: hasPermissionEscalate, loading: escalatePermissionLoading } = usePermission({
      module: fineGrainedPermissionsKeys.INVOICE.ESCALATE.module,
      action: fineGrainedPermissionsKeys.INVOICE.ESCALATE.action,
    });
    const { hasPermission: hasPermissionSetPriority, loading: setPriorityPermissionLoading } = usePermission({
      module: fineGrainedPermissionsKeys.INVOICE["SETPRIORITY/REMOVEPRIORITY"].module,
      action: fineGrainedPermissionsKeys.INVOICE["SETPRIORITY/REMOVEPRIORITY"].action,
    });

  const resetFilter = () => {
    const hasActiveFilters = Object.values(InvoiceApprovalFilters).some((value: any) => value?.length > 0);

    if (hasActiveFilters) {
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "company", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "division", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "stockist", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "status", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "assignee", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "city", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "district", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "state", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "fuidNo", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "sorting", value: [] }));
      dispatch(invoiceActions.setIsResetFilter(false));
    }
  };

  const searchParams = useMemo(
    () => ({
      company_code: InvoiceApprovalFilters?.company?.map((item) => item.code)?.join(",") || "",
      division_code: InvoiceApprovalFilters?.division?.map((item) => item.company_divisioncode)?.join(",") || "",
      startDate: InvoiceApprovalFilters?.dateRange?.from,
      endDate: InvoiceApprovalFilters?.dateRange?.to,
      assignee_id: InvoiceApprovalFilters?.assignee?.map((item) => item.invoice_assignment_id)?.join(",") || "",
      city: InvoiceApprovalFilters?.city?.map((item) => item.city_name?.split("|")?.[0]?.trim())?.join(",") || "",
      district: InvoiceApprovalFilters?.district?.map((item) => item.district_name)?.join(",") || "",
      state: InvoiceApprovalFilters?.state?.map((item) => item.state_name)?.join(",") || "",
      ordering: InvoiceApprovalFilters?.sorting?.[0]?.key || "DESC",
      fuid_status_id:
        searchValue && searchValue?.length > 0 ? "" : InvoiceApprovalFilters?.status?.map((item) => item.fuid_status_id)?.join(",") || "6",
      page_size: 10,
      page_number: 1,
      screen: "Approval",
      dateType: InvoiceApprovalFilters?.dateType?.[0]?.key || "",
      entered_by: InvoiceApprovalFilters?.enteredBy?.map((item) => item.entered_by)?.join(",") || "",
      approved_by: InvoiceApprovalFilters?.approvedBy?.map((item) => item.approved_by)?.join(",") || "",
      ...(searchValue && searchCategory ? { [searchCategory]: searchValue } : {}),
      ...(searchValue ? {} : { is_escalated: false })
    }),
    [InvoiceApprovalFilters, searchCategory, searchValue]
  );

  const fetchApprovalData = useCallback(
    async () => {
      setIsLoading(true);
      dispatch(invoiceActions.setInvoiceApprovalData([]));
      try {
        const allInvoicesRes = await fetchInvoicesData(searchParams);
        dispatch(invoiceActions.setInvoiceApprovalList(allInvoicesRes?.data?.data?.invoiceData || []));
        if (allInvoicesRes?.data?.data?.total > 0 && invoiceAnalysisData?.entry?.["All Invoices"] > 0) {
          dispatch(invoiceActions.setInvoiceAnalysisData(
            {
              entry: {
                "All Invoices": invoiceAnalysisData?.entry?.["All Invoices"] || 0,
                "Priority Tasks": invoiceAnalysisData?.entry?.["Priority Tasks"] || 0,
                "Escalated": invoiceAnalysisData?.entry?.["Escalated"] || 0,
              },
              approval: {
                "All Invoices": allInvoicesRes?.data?.data?.total || 0,
                "Priority Tasks": invoiceAnalysisData?.approval?.["Priority Tasks"] || 0,
                "Escalated": invoiceAnalysisData?.approval?.["Escalated"] || 0,
              }
            }
          ));
        }
        setPage(2);
        setHasMore(true);
        dispatch(invoiceActions.setCurrentApprovalInvoiceIndex(0));
        dispatch(invoiceActions.setCurrentApprovalPageIndex(1));
        toast({ description: "Invoice data fetched successfully", variant: "default" });
      } catch (error) {
        console.error("Error fetching invoices data:", error);
        toast({ description: "Failed to fetch invoice data", variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, searchParams]
  );

  const fetchMoreData = async () => {
    if (isLoading) return;
    try {
      const nextPageParams = { ...searchParams, page_number: page };
      const res = await fetchInvoicesData(nextPageParams);
      const newData = res?.data?.data?.invoiceData;
      if (!newData || newData.length === 0) {
        setHasMore(false);
        return;
      }
      dispatch(invoiceActions.setInvoiceApprovalList([...(invoiceApprovalList || []), ...newData]));
      dispatch(invoiceActions.setCurrentApprovalPageIndex(page));
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error("Error fetching more data:", error);
      toast({
        variant: "destructive",
        description: "Failed to load more invoices",
      });
      setHasMore(false);
    }
  };

  useEffect(() => {
    fetchApprovalData();
  }, [fetchApprovalData]);

  useEffect(() => {
    setPage(1);

    if (searchValue && searchValue?.trim()?.length > 0) {
      resetFilter();
    }
  }, [searchValue]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        {" "}
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mt-4 w-full pb-4">
      {invoiceApprovalList?.length > 0 && (
        <div
          id="scrollableDiv"
          className="h-[calc(100vh-250px)] overflow-auto overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
        >
          <InfiniteScroll
            dataLength={invoiceApprovalList?.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={""}
            scrollableTarget="scrollableDiv"
            endMessage={<div className="py-4 text-center text-gray-500">No more invoices to load</div>}
          >
            {invoiceApprovalList?.map((data: any, index: number) => (
              <li className="rounded-xl border shadow-sm border-gray-200 list-none my-4" key={index}>
                <InvoiceApprovalDetailCard
                  status={data?.fileStatusHdr?.fuid_status_desc}
                  isExpandable={true}
                  isExpandedInitial={false}
                  data={getInvoiceDetailsCardInfo(data)}
                  checkDataType={false}
                  invoiceData={data}
                  viewDetails={true}
                  index={index}
                  approvalScreen={true}
                  fetchApprovalData={fetchApprovalData}
                  hasViewDetailsPermission={hasViewDetailsPermission}
                  detailsPermissionLoading={detailsPermissionLoading}
                  hasPermissionReassign={hasPermissionReassign}
                  reassignPermissionLoading={reassignPermissionLoading}
                  hasPermissionPutOnHold={hasPermissionPutOnHold}
                  putOnHoldPermissionLoading={putOnHoldPermissionLoading}
                  hasPermissionReject={hasPermissionReject}
                  rejectPermissionLoading={rejectPermissionLoading}
                  hasPermissionEscalate={hasPermissionEscalate}
                  escalatePermissionLoading={escalatePermissionLoading}
                  hasPermissionSetPriority={hasPermissionSetPriority}
                  setPriorityPermissionLoading={setPriorityPermissionLoading}
                />
              </li>
            ))}
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
};

export default InvoiceApprovalContainer;
